import { useState } from 'react'

import { SearchSelect } from '@/components/SearchSelect'
import { Button, ErrorState, Field, inputClass } from '@/components/ui'
import { useSession } from '@/lib/session'
import { MilestoneField } from '@/features/delivery/MilestoneField'
import { AssigneePicker } from '@/features/people/AssigneePicker'
import { type AssigneeValue, assigneeFields } from '@/features/people/assignee'
import { projectOptions } from '@/features/projects/projectOptions'
import { useProjects } from '@/features/projects/useProjects'
import { TrackerSourceFields } from '@/features/workbench/TrackerSourceFields'
import { useCreateWorkItem } from '@/features/workbench/useWork'

const TYPES = [
  { value: 'task', label: 'Task' },
  { value: 'issue', label: 'Issue' },
  { value: 'change', label: 'Change' },
  { value: 'tracker', label: 'Tracker (long-running)' },
] as const

const POINTS = [1, 2, 3, 5, 8] as const

export function NewWorkItemDialog({
  sprintId,
  projectId = '',
  onClose,
}: {
  sprintId: string | null
  /** The Workbench's project filter, if one is set. A starting value, freely changed. */
  projectId?: string
  onClose: () => void
}) {
  const create = useCreateWorkItem()
  const { data: projectData } = useProjects({})
  const { data: session } = useSession()
  const [form, setForm] = useState({
    title: '',
    description: '',
    item_type: 'task',
    priority: 'medium',
    points: '',
    // Filtered to a project on the board means working on that project; starting the new
    // item there saves picking it again. Still an ordinary field.
    project_id: projectId,
    milestone_id: '',
    reference_url: '',
    source_path: '',
    source_sheet: '',
    source_status_column: 'Status',
    source_done_value: 'Completed',
  })

  // A pair, so it does not fit the single-string `set` helper below. See EditWorkItemDialog.
  const [assignee, setAssignee] = useState<AssigneeValue>({ userId: '', name: '' })

  const isTracker = form.item_type === 'tracker'

  const set = (key: keyof typeof form) => (event: { target: { value: string } }) =>
    setForm((current) => ({ ...current, [key]: event.target.value }))

  const submit = () => {
    create.mutate(
      {
        title: form.title,
        description: form.description,
        item_type: form.item_type,
        priority: form.priority,
        points: form.points ? Number(form.points) : null,
        project_id: form.project_id || null,
        milestone_id: form.milestone_id || null,
        // A tracker is long-running and never belongs to a sprint; the API rejects it
        // too, so this is the interface agreeing rather than the rule living here.
        sprint_id: isTracker ? null : sprintId,
        ...assigneeFields(assignee),
        reference_url: form.reference_url || null,
        source_path: isTracker ? form.source_path || null : null,
        source_sheet: isTracker ? form.source_sheet || null : null,
        source_status_column: isTracker ? form.source_status_column || null : null,
        source_done_value: isTracker ? form.source_done_value || null : null,
      },
      { onSuccess: onClose },
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="max-h-[92vh] w-full max-w-5xl overflow-auto rounded-lg bg-canvas p-6 shadow-2xl"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="New work item"
      >
        <h2 className="mb-4 font-heading text-2xl text-fg">New work item</h2>

        {create.error ? (
          <div className="mb-4">
            <ErrorState error={create.error} />
          </div>
        ) : null}

        <div className="grid gap-6 md:grid-cols-[minmax(0,1fr)_320px]">
            <div className="min-w-0 space-y-3">
              <Field label="Title">
                <input className={inputClass} value={form.title} onChange={set('title')} autoFocus />
              </Field>

              <Field label="Description" hint="What done looks like, and anything the assignee needs.">
                <textarea
                  className={`${inputClass} resize-y`}
                  rows={10}
                  value={form.description}
                  onChange={set('description')}
                />
              </Field>

              <Field label="Reference URL" hint="A change record, runbook or ticket.">
                <input className={inputClass} value={form.reference_url} onChange={set('reference_url')} />
              </Field>

              {isTracker ? (
                <TrackerSourceFields
                  value={form}
                  onChange={(key, next) => setForm((current) => ({ ...current, [key]: next }))}
                />
              ) : null}
            </div>

            <div className="min-w-0 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Type">
                  <select className={inputClass} value={form.item_type} onChange={set('item_type')}>
                    {TYPES.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Priority">
                  <select className={inputClass} value={form.priority} onChange={set('priority')}>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </Field>
              </div>

              <Field label="Project" hint="Optional — ad-hoc work needs no project.">
                <SearchSelect
                  value={form.project_id}
                  onChange={(value) =>
                    // A milestone belongs to one project, so changing project drops it.
                    setForm((current) => ({
                      ...current,
                      project_id: value,
                      milestone_id: value === current.project_id ? current.milestone_id : '',
                    }))
                  }
                  options={projectOptions(projectData?.projects ?? [], session?.userId)}
                  emptyLabel="None"
                />
              </Field>

              <MilestoneField
                projectId={form.project_id}
                value={form.milestone_id}
                onChange={(value) => set('milestone_id')({ target: { value } })}
              />

              <Field label="Points" hint={isTracker ? 'Weights the tracker in rollups.' : 'Optional.'}>
                <select className={inputClass} value={form.points} onChange={set('points')}>
                  <option value="">Unpointed</option>
                  {POINTS.map((point) => (
                    <option key={point} value={point}>
                      {point}
                    </option>
                  ))}
                </select>
              </Field>

              <AssigneePicker value={assignee} onChange={setAssignee} />
            </div>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!form.title.trim() || create.isPending} onClick={submit}>
            {create.isPending ? 'Creating…' : 'Create work item'}
          </Button>
        </div>
      </div>
    </div>
  )
}
